import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Send, Activity } from 'lucide-react';
import { sendInterrupt } from '@/services/chatService';
import { useTranslation } from 'react-i18next';

interface Props {
  conversationId: string;
  /** The assistant message currently streaming. */
  streamingMessageId: string;
}

/** Appears while a turn is in flight. Lets the user nudge the agent mid-
 *  stream — the server picks the interrupt up between iterations and injects
 *  it as a user message before the next model call. */
export function InterruptInput({ conversationId, streamingMessageId }: Props) {
  const { t } = useTranslation('common');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState<string | null>(null);

  const submit = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await sendInterrupt({
        conversationId,
        messageId: streamingMessageId,
        text,
      });
      setLastSent(text);
      setDraft('');
    } catch (e) {
      console.error('[interrupt] send failed:', e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-lg border border-synergy-blue/30 bg-synergy-blue/5 p-2 space-y-1.5">
      <div className="flex items-center gap-1.5 text-[11px] text-synergy-blue/80">
        <Activity className="h-3 w-3 animate-pulse" />
        {t('chat.interruptRunning')}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex gap-1.5"
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={sending}
          placeholder={t('chat.interruptPlaceholder')}
          className="flex-1 rounded-md border border-synergy-neutral-800 bg-synergy-neutral-950 px-2.5 py-1.5 text-xs text-synergy-text-primary placeholder:text-synergy-neutral-500 focus:outline-none focus:border-synergy-blue/60"
        />
        <Button
          type="submit"
          size="sm"
          disabled={sending || !draft.trim()}
          className="h-8 px-2.5"
        >
          <Send className="h-3 w-3" />
        </Button>
      </form>
      {lastSent && (
        <div className="text-[11px] text-synergy-neutral-400 truncate">
          {t('chat.sentPrefix', { text: lastSent })}
        </div>
      )}
    </div>
  );
}
